import React, { useState, useEffect, useCallback, useMemo } from "react";
import { PREMIUM_PRICE_PER_MONTH, TRIAL_CTA } from "../config/pricing";
import { TrackingService } from "../TrackingService";
import { useAuth } from "../context/AuthContext";
import { config } from "../Constants";
import {
    Box, Typography, Paper, CircularProgress,
    Button, Alert, Card, CardContent, Chip,
    Snackbar, useMediaQuery, useTheme, Grid
} from "@mui/material";
import { Link } from "react-router-dom";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { WorkspacePremium as PremiumIcon } from '@mui/icons-material';
import BulkActionToolbar from "../components/BulkActionToolbar";
import { EnhancedStockTable } from "../components/EnhancedStockTable/EnhancedStockTable";
import StockFilters from "../components/StockFilters/StockFilters";
import PriceGroups from "../components/PriceGroups";
import { CARDS_PER_PAGE } from "../utils/listingDefaults";

const URL = config.url;

export const MyTrackedStocksPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [stocks, setStocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isBulkUntracking, setIsBulkUntracking] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: CARDS_PER_PAGE });
    const { isAuthenticated, isPremium, login, user } = useAuth();

    const canUseTrial = !user?.hasUsedFreeTrial;

    const pendingTrackings = useMemo(() => stocks.filter(stock => !stock.isExecuted), [stocks]);
    const executedTrackings = useMemo(() => stocks.filter(stock => stock.isExecuted), [stocks]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const fetchTrackingData = useCallback(async () => {
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            const trackingData = await TrackingService.getUserTrackedStocks();

            if (trackingData.length === 0) {
                setStocks([]);
                setLoading(false);
                return;
            }

            const skus = trackingData.map(tracking => tracking.stockSku);

            const stocksPromises = skus.map(sku =>
                fetch(`${URL}/api/v1/stocks/sku/${sku}`)
                    .then(res => res.json())
                    .then(data => {
                        if (data.result === "SUCCESS" && data.data) {
                            const tracking = trackingData.find(t => t.stockSku === sku);
                            return {
                                ...data.data,
                                trackingId: tracking.id,
                                isExecuted: tracking.executed,
                                executedAt: tracking.executedAt,
                                addToCartUrl: tracking.addToCartUrl || data.data.addToCartUrl
                            };
                        }
                        return null;
                    })
                    .catch(err => {
                        console.error(`Error fetching stock with SKU ${sku}:`, err);
                        return null;
                    })
            );

            const stocksData = await Promise.all(stocksPromises);
            setStocks(stocksData.filter(s => s !== null));
        } catch (error) {
            console.error("Error fetching tracked stocks:", error);
            setError("Failed to load your tracked products. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchTrackingData();
    }, [fetchTrackingData]);

    useEffect(() => {
        setPaginationModel((previous) => ({
            ...previous,
            page: 0
        }));
    }, [filters, searchTerm]);

    const handleBulkUntrackClick = async () => {
        setIsBulkUntracking(true);
        try {
            const removedCount = await TrackingService.removeBulkTracking(selectedRows);
            const requestedCount = selectedRows.length;

            if (removedCount < requestedCount) {
                const failedCount = requestedCount - removedCount;
                showSnackbar(
                    `Untracked ${removedCount} product(s). ${failedCount} could not be removed.`,
                    'warning'
                );
            } else {
                showSnackbar(
                    `Successfully untracked ${removedCount} product${removedCount !== 1 ? 's' : ''}!`,
                    'success'
                );
            }

            setSelectedRows([]);
            await fetchTrackingData();
        } catch (error) {
            console.error('Error with bulk untracking:', error);
            showSnackbar(error.message || 'Failed to untrack products', 'error');
        } finally {
            setIsBulkUntracking(false);
        }
    };

    const handleClearSelection = () => {
        setSelectedRows([]);
    };

    const handleClearFilters = () => {
        setFilters({});
        setSearchTerm('');
        setPaginationModel((previous) => ({
            ...previous,
            page: 0
        }));
    };

    if (!isAuthenticated) {
        return (
            <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        Sign in to Track Products
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Sign in and start tracking your favorite products.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => login()}
                    >
                        Sign in
                    </Button>
                </Paper>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (!isPremium) {
        return (
            <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <PremiumIcon sx={{ fontSize: 56, color: 'gold', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        Premium Membership Required
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Upgrade to premium to track unlimited products.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        component={Link}
                        to="/subscription"
                        startIcon={<PremiumIcon />}
                    >
                        {canUseTrial ? TRIAL_CTA : `Upgrade to Premium - ${PREMIUM_PRICE_PER_MONTH}`}
                    </Button>
                </Paper>
            </Box>
        );
    }

    const handleTrackingChange = (sku, isTracked) => {
        if (!isTracked) {
            setStocks((prev) => prev.filter((stock) => stock.sku !== sku));
            setSelectedRows((prev) => prev.filter((rowSku) => rowSku !== sku));
        }
    };

    return (
        <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <PriceGroups onNotify={(message, severity) => setSnackbar({ open: true, message, severity })} />

            {stocks.length === 0 && !loading && !error ? (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        You're not tracking any products yet
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Start tracking products now.
                    </Typography>
                    <Button
                        variant="contained"
                        component={Link}
                        to="/stocks"
                        startIcon={<ShoppingCartIcon />}
                    >
                        Products
                    </Button>
                </Paper>
            ) : (
                <>
                    {pendingTrackings.length > 0 && (
                        <>
                            <Typography variant="h5" sx={{ mt: 1, mb: 2 }}>
                                Active Tracking ({pendingTrackings.length})
                            </Typography>

                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                    sx={{
                                        position: { md: 'sticky' },
                                        top: { md: 112 },
                                        alignSelf: { md: 'flex-start' }
                                    }}
                                >
                                    <StockFilters
                                        filters={filters}
                                        onFiltersChange={setFilters}
                                        onClearFilters={handleClearFilters}
                                        onSearch={setSearchTerm}
                                        initialSearchValue={searchTerm}
                                        showManufacturer={false}
                                        showPrice={false}
                                        showOnSale={false}
                                        showDateRange={false}
                                        layout="sidebar"
                                    />

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        component={Link}
                                        to="/stocks"
                                        startIcon={<BookmarkIcon />}
                                        fullWidth
                                        sx={{ mt: 2 }}
                                    >
                                        Track Products
                                    </Button>
                                </Grid>

                                <Grid item xs={12} md={9}>
                                    <BulkActionToolbar
                                        selectedCount={selectedRows.length}
                                        onAction={handleBulkUntrackClick}
                                        onClear={handleClearSelection}
                                        isLoading={isBulkUntracking}
                                        actionType="untrack"
                                    />

                                    <EnhancedStockTable
                                        stock={pendingTrackings}
                                        filters={filters}
                                        searchTerm={searchTerm}
                                        displayMode="cards"
                                        tableTitle="Tracked Products"
                                        itemLabel="tracked products"
                                        paginationModel={paginationModel}
                                        onPaginationModelChange={setPaginationModel}
                                        enableCardSelection={true}
                                        cardSelection={selectedRows}
                                        onCardSelectionChange={setSelectedRows}
                                        onTrackingChange={handleTrackingChange}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {executedTrackings.length > 0 && (
                        <>
                            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
                                Reserved Products ({executedTrackings.length})
                            </Typography>

                            {executedTrackings.map(stock => (
                                <Card key={stock.sku} sx={{ mb: 2, borderRadius: 2, boxShadow: 2 }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                                            {stock.thumbnailImage && (
                                                <Box sx={{ flexShrink: 0, mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}>
                                                    <img
                                                        src={stock.thumbnailImage}
                                                        alt={stock.name}
                                                        style={{ width: 60, height: 60, objectFit: 'contain' }}
                                                    />
                                                </Box>
                                            )}

                                            <Box sx={{ flexGrow: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                                                <Typography variant="h6" component="div">
                                                    {stock.name}
                                                </Typography>

                                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, justifyContent: { xs: 'center', sm: 'flex-start' }, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label="Reserved"
                                                        color="success"
                                                        size="small"
                                                        sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}
                                                    />

                                                    <Typography variant="body2" color="text.secondary">
                                                        Reserved on: {new Date(stock.executedAt).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{ mt: { xs: 2, sm: 0 } }}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    href={stock.addToCartUrl}
                                                    target="_blank"
                                                    startIcon={<ShoppingCartIcon />}
                                                    fullWidth={isMobile}
                                                >
                                                    Go to Cart
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </>
                    )}
                </>
            )}
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};
